import React, { memo, useCallback, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import type {
  BankOnboardingFormValues,
  BeneficialOwnerFormValues,
} from 'src/interfaces/bankOnboarding'
import { getBankOnboardingSubmitting } from 'src/selectors/bankOnboarding'
import { buildEmptyBeneficialOwner } from 'src/utils/paymentsOps/bankOnboardingForm'
import { BeneficialOwnerCard } from '../BeneficialOwnerCard'
import { BeneficialOwnerModal } from '../BeneficialOwnerModal'

interface EditingOwner {
  index: number | null // null = adding
  owner: BeneficialOwnerFormValues
}

const GENERIC_OWNERS_ERROR = 'Fix the beneficial owners to continue'

const BeneficialOwnersStepComponent = () => {
  const { values, errors, touched, setFieldValue, setFieldError } =
    useFormikContext<BankOnboardingFormValues>()
  const isSubmitting = useSelector(getBankOnboardingSubmitting)
  const [editing, setEditing] = useState<EditingOwner | null>(null)

  const handleAdd = useCallback(() => {
    setEditing({ index: null, owner: buildEmptyBeneficialOwner() })
  }, [])

  const handleEdit = useCallback(
    (index: number) => {
      const owner = values.beneficialOwners[index]

      if (!owner) {
        return
      }

      setEditing({ index, owner })
    },
    [values.beneficialOwners],
  )

  const handleRemove = useCallback(
    (index: number) => {
      setFieldValue(
        'beneficialOwners',
        values.beneficialOwners.filter((_owner, ownerIndex) => {
          return ownerIndex !== index
        }),
      )
      setFieldError('beneficialOwners', undefined)
    },
    [setFieldError, setFieldValue, values.beneficialOwners],
  )

  const handleSave = useCallback(
    (owner: BeneficialOwnerFormValues) => {
      if (editing === null) {
        return
      }

      if (editing.index === null) {
        setFieldValue('beneficialOwners', [...values.beneficialOwners, owner])
      } else {
        setFieldValue(
          'beneficialOwners',
          values.beneficialOwners.map((existing, ownerIndex) => {
            return ownerIndex === editing.index ? owner : existing
          }),
        )
      }

      // The list changed — a stale "add at least one" / "owner N incomplete" must not linger.
      setFieldError('beneficialOwners', undefined)
      setEditing(null)
    },
    [editing, setFieldError, setFieldValue, values.beneficialOwners],
  )

  const handleClose = useCallback(() => {
    setEditing(null)
  }, [])

  if (!values.hasAnyBeneficialOwner) {
    return (
      <Typography variant="body2" css={tw`text-shades-500`}>
        The business has no beneficial owners (set on the Business step) — none will be sent.
      </Typography>
    )
  }

  // The validator reports a single string on the list; never render anything else.
  const rawError: unknown = errors.beneficialOwners
  let listError: string | undefined

  if (Boolean(touched.beneficialOwners) && rawError) {
    listError = typeof rawError === 'string' ? rawError : GENERIC_OWNERS_ERROR
  }

  return (
    <div css={tw`flex flex-col gap-3`}>
      <Typography variant="body2" css={tw`text-shades-500`}>
        Individuals who own 25% or more of the business.
      </Typography>
      {values.beneficialOwners.map((owner, index) => {
        return (
          <BeneficialOwnerCard
            key={`${owner.firstName}-${owner.lastName}-${index}`}
            owner={owner}
            index={index}
            onEdit={handleEdit}
            onRemove={handleRemove}
          />
        )
      })}
      {listError ? (
        <Typography variant="body2" css={tw`text-xs text-critical-500`}>
          {listError}
        </Typography>
      ) : null}
      <div>
        <Button size="small" color="white" onClick={handleAdd} data-testid="onboarding-add-owner">
          + Add beneficial owner
        </Button>
      </div>
      <BeneficialOwnerModal
        owner={editing?.owner ?? null}
        isNew={editing?.index === null}
        isLocked={isSubmitting}
        onSave={handleSave}
        onClose={handleClose}
      />
    </div>
  )
}

export const BeneficialOwnersStep = memo(BeneficialOwnersStepComponent)
